import React, { useMemo } from 'react';
import { TouchableOpacity } from 'react-native';
import { Appbar, Avatar } from 'react-native-paper';
import { useRouter, usePathname, useNavigation } from 'expo-router';
import styled from '@emotion/native';
import { accent, green, sand, white } from '@/shared/styles/colors';
import { ROUTES } from '@/shared/constants/routes';
import { IS_WEB } from '@/shared/constants/utility';
import { MOBILE_HEADER_HEIGHT, ZIndex } from '@/shared/styles/constants';
import useStore from '@/stores/useStore';
import { useGetUser } from '@/shared/hooks/useGetUser';
import { RouteType } from '@/shared/types/navigation';
import { DrawerActions } from '@react-navigation/native';
import { useInstantStylingToggle } from '@/shared/hooks/useInstantStylingToggle';
import { formatNameWithLastInitial } from '@/shared/utils';
import { useIsMessagesChannel } from '@/shared/hooks/useIsMessagesChannel';
import { isPathMatching } from '@/shared/utils';
import useInstantStylingStore from '@/stores/useInstantStylingStore';
import { useShallow } from 'zustand/react/shallow';
import useUnreadNotifications from '@/shared/hooks/useUnreadNotifications';
import { AVATAR, createResizedPhotoUrl } from '@/shared/utils/photoUtils';
import { useEventConfiguration } from '@/shared/hooks/useEventConfiguration';
import EventBanner from '@/components/explore/EventBanner';

const MobileHeader = () => {
  const router = useRouter();
  const pathname = usePathname();
  const navigation = useNavigation();
  const { user } = useGetUser();
  const { hasUnreadNotifications } = useUnreadNotifications();
  const { inPersonEventSessions } = useEventConfiguration();

  const isInstantStylingActive = useInstantStylingStore(
    (state) => state.isInstantStylingActive,
  );
  const { toggleInstantStyling } = useInstantStylingToggle();

  // Optimize multiple store selectors with useShallow
  const { isStylist, toggleMobileMenu, isMobileMenuVisible } = useStore(
    useShallow((state) => ({
      isStylist: state.isStylist,
      toggleMobileMenu: state.toggleMobileMenu,
      isMobileMenuVisible: state.isMobileMenuVisible,
    })),
  );

  const shouldShowEventBanner = useMemo(() => {
    return (
      inPersonEventSessions && pathname === ROUTES.EXPLORE.path && !isStylist
    );
  }, [inPersonEventSessions, pathname, isStylist]);

  const isInChat = useIsMessagesChannel();

  const resizedProfilePicUrl = useMemo(() => {
    return user?.profilePicUrl
      ? createResizedPhotoUrl(user.profilePicUrl, AVATAR)
      : '';
  }, [user?.profilePicUrl]);

  const renderAvatar = () => {
    const userName = formatNameWithLastInitial(user).toUpperCase();

    if (resizedProfilePicUrl && !isMobileMenuVisible) {
      return (
        <Avatar.Image
          size={45}
          source={{ uri: resizedProfilePicUrl }}
          style={{ backgroundColor: green }}
        />
      );
    }

    return (
      <Avatar.Text
        size={42}
        labelStyle={{ fontSize: 16 }}
        color={white}
        style={{ backgroundColor: green }}
        label={
          userName
            ? userName
                .split(' ')
                .map((n) => n[0])
                .join('')
            : '?'
        }
      />
    );
  };

  const handleToggleInstantStyling = async () => {
    const newInstantStylingState = await toggleInstantStyling();

    if (!newInstantStylingState) return;

    router.navigate(ROUTES.INSTANT_STYLING.path);
  };

  const onPressProfileIcon = () => {
    if (IS_WEB) {
      toggleMobileMenu();
    } else {
      navigation.dispatch(DrawerActions.openDrawer());
    }
  };

  const isRouteActive = (route: RouteType) => {
    return isPathMatching(route.path, pathname);
  };

  const isInstantStylingScreen = pathname === ROUTES.INSTANT_STYLING.path;
  const showAlternateHeader = isInstantStylingScreen && !isStylist;
  const isVideoSession = pathname === ROUTES.VIDEO_SESSION.path;
  const shouldHideHeader = isVideoSession || isInChat;

  const LogoPath = isStylist ? ROUTES.SESSIONS.path : ROUTES.EXPLORE.path;
  const logoButtonAccessibilityLabel = isStylist
    ? ROUTES.SESSIONS.label
    : ROUTES.EXPLORE.label;

  return (
    <HeaderContainer style={{ display: shouldHideHeader ? 'none' : 'flex' }}>
      {shouldShowEventBanner && <EventBanner />}
      <Header
        backgroundColor={showAlternateHeader ? green : sand}
        statusBarHeight={0}
      >
        <TouchableOpacity
          onPress={() => router.push(LogoPath)}
          accessibilityLabel={`View ${logoButtonAccessibilityLabel}`}
        >
          <StyledImage
            source={
              showAlternateHeader
                ? require('@/assets/images/EmmeLogoSmWh.png')
                : require('@/assets/images/EmmeLogoSmGr.png')
            }
            accessibilityLabel="Emme Logo"
            resizeMode="contain"
          />
        </TouchableOpacity>
        <NavControls>
          {isStylist && !IS_WEB && (
            <ToggleButton
              onPress={handleToggleInstantStyling}
              accessibilityLabel={
                isInstantStylingActive
                  ? 'Disable Instant Styling'
                  : 'Enable Instant Styling'
              }
            >
              <ToggleImage
                source={
                  isInstantStylingActive
                    ? require('@/assets/images/InstantToggleOnSm.png')
                    : require('@/assets/images/InstantToggleOffSm.png')
                }
                resizeMode="contain"
                alt={`Instant styling ${isInstantStylingActive ? 'on' : 'off'}`}
              />
            </ToggleButton>
          )}
          <AppbarAction
            // TODO(154): Show chat alert icon when user has unread messages
            icon={require('@/assets/images/ChatIcon.png')}
            iconColor={green}
            rippleColor="transparent"
            size={21}
            onPress={() => router.push(ROUTES.MESSAGES.path)}
            accessibilityLabel="View Messages"
            backgroundColor={
              isRouteActive(ROUTES.MESSAGES) ? accent : 'transparent'
            }
          />
          <AppbarAction
            icon={
              hasUnreadNotifications
                ? require('@/assets/images/NotificationIconAlert.png')
                : require('@/assets/images/NotificationIcon.png')
            }
            iconColor={green}
            rippleColor="transparent"
            size={22}
            onPress={() => router.push(ROUTES.NOTIFICATIONS.path)}
            accessibilityLabel="View Notifications"
            backgroundColor={
              isRouteActive(ROUTES.NOTIFICATIONS) ? accent : 'transparent'
            }
          />
          {IS_WEB ? (
            <AppbarAction
              icon={require('@/assets/images/HamburgerIcon.png')}
              onPress={toggleMobileMenu}
              color={isMobileMenuVisible ? white : green}
              backgroundColor={isMobileMenuVisible ? green : 'transparent'}
            />
          ) : (
            <TouchableOpacity onPress={onPressProfileIcon}>
              {renderAvatar()}
            </TouchableOpacity>
          )}
        </NavControls>
      </Header>
    </HeaderContainer>
  );
};

export default MobileHeader;

const HeaderContainer = styled.View({
  width: '100%',
  position: 'relative',
  overflow: 'visible',
  zIndex: ZIndex.MiddleLayer,
  backgroundColor: 'transparent',
});

const Header = styled(Appbar.Header)<{ backgroundColor?: string }>(
  ({ backgroundColor }) => ({
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: IS_WEB ? 10 : 0,
    paddingBottom: 14,
    paddingLeft: 16,
    paddingRight: 16,
    backgroundColor,
    height: MOBILE_HEADER_HEIGHT,
    borderBottomColor: accent,
    borderBottomWidth: 2,
  }),
);

const StyledImage = styled.Image({
  height: 16,
  width: 73,
});

const NavControls = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
});

const ToggleButton = styled.TouchableOpacity({
  alignItems: 'center',
  justifyContent: 'center',
  paddingRight: 10,
});

const ToggleImage = styled.Image({
  width: 120,
  height: 36,
});

const AppbarAction = styled(Appbar.Action)<{ backgroundColor?: string }>(
  ({ backgroundColor = 'transparent' }) => ({
    margin: 0,
    borderRadius: 50,
    borderWidth: 0,
    backgroundColor: backgroundColor,
  }),
);
